import React, { useRef, useEffect, useState } from "react";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import ScrollProgress from "@/components/ScrollProgress";
import Marquee from "@/components/Marquee";
import ProcessSteps from "@/components/ProcessSteps";
import TextRevealMatrix from "@/components/TextRevealMatrix";
import Features from "@/components/Features";
import Carousel from "@/components/Carousel";
import Pricing from "@/components/Pricing";
import CTA from "@/components/CTA";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Clock, Star } from "lucide-react";

export default function Landing() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    video.currentTime = 0;

    const onMeta = () => {};
    const onError = () => {
      console.warn("[Landing] Background video failed to load — using fallback color");
    };
    const onScroll = () => {
      if (!video.duration || isNaN(video.duration)) return;
      const scrolled = window.scrollY;
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const ratio = Math.min(1, scrolled / maxScroll);
      try {
        video.currentTime = ratio * video.duration * 0.92;
      } catch (_) {}
    };

    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("error", onError);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("error", onError);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div
      className="min-h-screen text-foreground"
      style={{ background: "#0b0b0b" }}
      data-testid="landing-page"
    >
      <ScrollProgress />

      {/* ── Scroll-driven video background ── */}
      <video
        ref={videoRef}
        src="/bmw-bg.mp4"
        className="fixed inset-0 w-full h-full object-cover pointer-events-none select-none"
        style={{
          zIndex: 0,
          filter: "brightness(0.55) saturate(0.95) contrast(1.05)",
          background: "#0b0b0b",
          opacity: 1,
        }}
        muted
        playsInline
        webkit-playsinline="true"
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
      />

      {/* Bottom-only vignette (no top dark overlay) */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          background:
            "linear-gradient(180deg, transparent 0%, transparent 55%, rgba(0,0,0,0.55) 90%, rgba(0,0,0,0.80) 100%)",
        }}
      />

      {/* ── Page content ── */}
      <div style={{ position: "relative", zIndex: 10 }}>
        <Navigation />
        <Hero />

        <MarqueeStrip />

        <StatementBand />

        <div className="section">
          <ProcessSteps />
        </div>

        <Features />
        <Carousel />
        <Pricing />
        <CTA />
        <Footer />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Marquee of trust items (subtle Apple-style ribbon)
────────────────────────────────────────────── */
function MarqueeStrip() {
  const items = [
    { icon: Sparkles, label: "Paint-safe" },
    { icon: ShieldCheck, label: "Fully insured" },
    { icon: Clock, label: "2–3 hour service" },
    { icon: Star, label: "5-star rated" },
    { icon: ShieldCheck, label: "Satisfaction guaranteed" },
    { icon: Clock, label: "9 AM – 10 PM" },
    { icon: Star, label: "200+ Niskayuna cars" },
  ];
  return (
    <div
      className="relative border-y border-white/[0.05] py-7 my-4"
      data-testid="trust-marquee"
    >
      <Marquee
        items={items.map((it) => (
          <div
            key={it.label}
            className="flex items-center gap-3 text-muted"
            style={{ letterSpacing: "0.02em" }}
          >
            <it.icon size={14} style={{ color: "#8A8A8A" }} />
            <span
              className="text-[12px] tracking-[0.18em] uppercase font-mono"
              style={{ color: "rgba(245,245,247,0.6)" }}
            >
              {it.label}
            </span>
            <span style={{ color: "rgba(138,138,138,0.30)", marginLeft: 24 }}>✦</span>
          </div>
        ))}
        speed={50}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Apple-style statement band
────────────────────────────────────────────── */
function StatementBand() {
  return (
    <section
      className="relative section-sm"
      data-testid="statement-band"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="overline mb-6 text-gold-bright"
        >
          Why Nisky
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-semibold tracking-[-0.04em] leading-[1.05]"
          style={{ fontSize: "clamp(2rem, 6vw, 4.4rem)" }}
        >
          <TextRevealMatrix
            text="Paint-safe products, two-bucket method, and zero shortcuts — every single visit."
          />
        </motion.h2>
      </div>
    </section>
  );
}
